import type React from "react"
import { useState } from "react"
import { Sizes, Weights } from "../../../../interfaces/sizing"
import { Typography } from "../../../common"
import "./Tabs.scss"

export interface ITabItem {
    /** The id of the tab (used to identify the active tabs, therefore each tab should have a unique ID) */
    tabId: string
    /** The text that is used as the tab's label */
    text: string
    /** The component that is supposed to render when the tab is currently active. */
    TabComponent: React.ReactNode
}

interface TabProps {
    /** Array of tabs */
    tabs: ITabItem[]
}

/**
 * Tabs is a standalone component that displays multiple tabs, and allows switching
 * between the content of each tab.
 */
export function Tabs({ tabs }: TabProps) {
    const [activeTabId, setActiveTabId] = useState<string>(tabs[0].tabId)

    /** Renders the tab component based off the active tabs */
    const renderTabComponent = (): React.ReactNode => {
        return tabs.filter((tabItem) => tabItem.tabId === activeTabId)[0]
            .TabComponent
    }

    return (
        <div className="Tabs">
            <div className="tabsWrapper">
                <div className="tabLinksContainer">
                    {tabs.map((tabItem) => (
                        <Typography
                            weight={Weights.medium}
                            size={Sizes.xs}
                            component="button"
                            className={`tabLink ${
                                activeTabId === tabItem.tabId ? "tabActive" : ""
                            }`}
                            onClick={() => setActiveTabId(tabItem.tabId)}
                            key={tabItem.tabId}
                            data-testid={tabItem.tabId}
                        >
                            {tabItem.text}
                        </Typography>
                    ))}
                </div>
            </div>
            <div className="tabContentContainer">{renderTabComponent()}</div>
        </div>
    )
}
